import { TooltipProps, Tooltip, tooltipClasses } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { VendorTextBlock } from './VendorTextBlock';

type Props = {
  timestamp: number;
  itemCount: number;
  cartVendor: string;
  displaySourceSite?: string;
  vendorDisplayName?: string;
  includeMarginRight?: boolean;
  cartStore?: string;
  showPriceDateDisclaimer?: boolean;
  darkText?: boolean;
};

/**
 * This component generally renders:
 * "This is a/an <vendor> list. Prices as of <date> (hoverable tooltip)"
 * It can be configured to only render the "Prices as of <date>" part by setting showPriceDateDisclaimer to false.
 * It can be configured to render the "This is a/an <vendor> list" part by setting vendorDisplayName to a value.
 */
export const VendorListAndPriceDisc = ({
  timestamp,
  itemCount,
  cartVendor,
  displaySourceSite,
  vendorDisplayName,
  cartStore,
  includeMarginRight = true,
  showPriceDateDisclaimer = true,
  darkText = false,
}: Props) => {
  const { t } = useTranslation();

  return (
    <div
      className={`text-100 z-[1] inline-flex w-fit flex-grow items-center justify-end gap-2 xs:justify-start ${includeMarginRight ? 'smMed:mr-24 sm:mr-0' : ''}`}
    >
      <span
        style={{ overflowWrap: 'anywhere' }}
        className={`whitespace-normal text-balance text-right ${darkText ? 'text-primary-dark' : 'text-blueGray'}`}
      >
        {vendorDisplayName && (
          <>
            <VendorTextBlock
              vendorDisplayName={vendorDisplayName}
              displaySourceSite={displaySourceSite}
              cartStore={cartStore}
              cartVendor={cartVendor}
            />
            &nbsp;
          </>
        )}
        {showPriceDateDisclaimer && (
          <>
            {t('common.prices_as_of', { count: itemCount })}&nbsp;
            {dayjs(timestamp).format('M/D/YYYY, h:mm A')}
          </>
        )}
      </span>
      {showPriceDateDisclaimer && (
        <DisclaimerTooltip
          title={
            <p>
              <Trans i18nKey={t('common.price_disclaimer_tooltip')} components={[<br />, <br />]} />
            </p>
          }
          placement='top'
          arrow
        >
          <div className='flex h-[22.4px] items-center justify-center fill-offGray hover:fill-gray-300'>
            <InfoOutlinedIcon
              className='transition-all'
              style={{ fontSize: 20, fill: 'inherit' }}
            />
          </div>
        </DisclaimerTooltip>
      )}
    </div>
  );
};

export const DisclaimerTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#F1F1F1',
    color: '#04033C',
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#3032DC',
    padding: 16,
    fontFamily: 'Open Sans',
    marginInline: 4,
    marginBottom: '9px !important',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#F1F1F1',
    fontSize: 20,
    '&:before': {
      border: '2px solid #3032DC',
    },
  },
  [`& .${tooltipClasses.popper}`]: {
    color: '#F1F1F1',
  },
  [`& .${tooltipClasses.popperArrow}`]: {
    color: '#F1F1F1',
  },
}));
